// mockAPI.js
// Previously hit Django at http://localhost:8000.
// Django is removed: account creation uses Firebase Auth; data writes go to Cloud Functions.

import backendAPI from "../utils/backendAPI";
import { cacheMerchantBusinessName } from "../utils/merchantContext";
import { firebaseAuth } from "../firebase/firebaseClient";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
    ensureMerchantRewardsSubcollection,
    upsertMerchantProgramReward,
} from "../utils/merchantRewardsFirestore";
import { getMerchantCheckoutContext } from "../utils/merchantContext";

function friendlyFirebaseError(error) {
    const code = String(error?.code || "");
    const lookup = {
        "auth/email-already-in-use": "An account with this email already exists. Please log in instead.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/weak-password": "Password is too weak. Please choose a stronger password.",
        "auth/network-request-failed": "Network error. Please check your connection and try again.",
        "auth/too-many-requests": "Too many attempts. Please try again in a few minutes.",
    };
    return lookup[code] || error?.message || "Failed to create account";
}

function friendlyRequestError(error, fallback) {
    const code = String(error?.code || "");
    if (code === "ERR_NETWORK") {
        return "Cannot reach the API server. Start Cloud Functions locally (cd functions && npm run serve) or check REACT_APP_FUNCTIONS_BASE_URL in frontend/.env.";
    }
    return error?.response?.data?.error || error?.response?.data?.detail || error?.message || fallback;
}

function firstErrorString(data) {
    if (!data || typeof data !== "object") return "";
    for (const value of Object.values(data)) {
        if (typeof value === "string" && value.trim()) return value.trim();
        if (Array.isArray(value) && value.length && typeof value[0] === "string") {
            return String(value[0]).trim();
        }
    }
    return "";
}

async function registerMerchantProfile(idToken, { email, password, referralToken }) {
    const body = {
        email,
        password,
        accountType: "merchant",
        acceptedTerms: true,
    };
    if (referralToken) body.referralToken = referralToken;
    const regRes = await backendAPI.post("/api/auth/register/", body, {
        headers: { Authorization: `Bearer ${idToken}` },
    });
    return String(regRes?.data?.uid || "").trim();
}

function persistAuthSession({ uid, email, idToken, backendUserId }) {
    localStorage.setItem("authToken", idToken);
    if (backendUserId) localStorage.setItem("userId", backendUserId);
    if (email) localStorage.setItem("userEmail", email);
    localStorage.setItem(
        "userInfo",
        JSON.stringify({
            uid,
            email,
            idToken,
            access: idToken,
            id: backendUserId || "",
        })
    );
}

export const mockAPI = {
    registerMerchantProfile,

    // 1. Create account
    async createAccount(data) {
        console.log('📤 API: Creating account', { email: data.email })
        try {
            const email = String(data.email || "").trim();
            const password = String(data.password || "");

            const cred = await createUserWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            const idToken = await cred.user.getIdToken();
            const referralToken = String(localStorage.getItem("klerk_referral_token") || "").trim();

            let backendUserId = cred.user.uid;
            let registerWarning = "";
            try {
                const registeredUid = await registerMerchantProfile(idToken, {
                    email,
                    password,
                    referralToken,
                });
                if (registeredUid) backendUserId = registeredUid;
            } catch (registerError) {
                registerWarning = friendlyRequestError(
                    registerError,
                    "Account created, but we could not reach the server to finish setup."
                );
                console.warn("Merchant profile registration failed after Firebase signup:", registerError);
            }

            persistAuthSession({
                uid: cred.user.uid,
                email,
                idToken,
                backendUserId,
            });

            return {
                success: true,
                userId: backendUserId,
                authToken: idToken,
                registerWarning,
            }
        } catch (error) {
            if (String(error?.code || "").startsWith("auth/")) {
                throw new Error(friendlyFirebaseError(error));
            }
            const data = error?.response?.data;
            const details = firstErrorString(data);
            if (details && /already exists|already in use|already been taken/i.test(details)) {
                throw new Error("An account with this email already exists. Please log in instead.");
            }
            throw new Error(friendlyRequestError(error, "Failed to create account"));
        }
    },

    // 2. Save business info
    async saveBusiness(data) {
        console.log('📤 API: Saving business', data)
        try {
            const response = await backendAPI.post('/api/business/create/', {
                businessName: data.businessName,
                address: data.address,
                category: data.category,
                phone: data.phone,
                googlePlaceId: String(data.googlePlaceId || '').trim(),
                freeVisitForReview: Boolean(data.freeVisitForReview),
            })
            
            const businessId = response.data.business_id;
            if (businessId) {
                localStorage.setItem("merchant_id", String(businessId));
                localStorage.setItem("business_id", String(businessId));
            }
            cacheMerchantBusinessName(data.businessName);
            await ensureMerchantRewardsSubcollection(businessId);
            return {
                success: true,
                businessId,
            }
        } catch (error) {
            throw new Error(error?.response?.data?.error || error?.message || 'Failed to save business')
        }
    },

    // 3. Save reward settings
    async saveReward(data) {
        console.log('📤 API: Saving reward', data)
        const businessId = String(
            data.businessId || localStorage.getItem("merchant_id") || ""
        ).trim();
        const visits = Math.max(1, parseInt(data.visits, 10) || 6);
        const rewardDescription = String(data.rewardDescription || "").trim();
        const rewardType = String(data.rewardType || "").trim() || rewardDescription;

        if (!businessId) {
            throw new Error("Business ID missing. Go back and save your business info first.");
        }
        if (!rewardType) {
            throw new Error("Please enter a reward type or description.");
        }

        try {
            const response = await backendAPI.post("/api/loyalty/create/", {
                businessId,
                visits,
                rewardDescription,
                rewardType,
            });

            const loyaltyId = response.data.loyalty_id || businessId;
            const rewardId = response.data.reward_id;
            if (loyaltyId) localStorage.setItem("merchant_id", String(loyaltyId));
            if (rewardId && businessId) {
                localStorage.setItem(`klerk_program_reward_doc_id:${businessId}`, String(rewardId));
            }
            return {
                success: true,
                loyaltyId,
                rewardId,
            };
        } catch (error) {
            console.warn("loyalty/create failed, trying Firestore client fallback:", error);
            try {
                await upsertMerchantProgramReward(businessId, {
                    visits,
                    rewardType,
                    rewardDescription,
                });
                return {
                    success: true,
                    loyaltyId: businessId,
                    rewardId: localStorage.getItem(`klerk_program_reward_doc_id:${businessId}`),
                    usedClientFallback: true,
                };
            } catch (fallbackErr) {
                console.warn("Client Firestore reward fallback failed:", fallbackErr);
            }
            throw new Error(
                friendlyRequestError(
                    error,
                    "Failed to save reward. Start Cloud Functions (cd functions && npm run serve) and try again."
                )
            );
        }
    },

    // 4. Create Stripe checkout
    async createCheckout(data) {
        console.log('📤 API: Creating Stripe checkout', data)
        try {
            const ctx = getMerchantCheckoutContext(null);
            const merchantId = String(data.businessId || data.merchantId || ctx.merchantId || "").trim();
            const merchantEmail = String(data.merchantEmail || ctx.merchantEmail || "").trim();
            const businessName = String(data.businessName || ctx.businessName || localStorage.getItem("business_name") || "").trim();

            if (!merchantId || !merchantEmail || !businessName) {
                throw new Error("Missing checkout fields: merchantId, merchantEmail, and businessName are required.");
            }

            localStorage.setItem("merchant_id", merchantId);
            localStorage.setItem("business_id", merchantId);

            const response = await backendAPI.post('/api/stripe/create-checkout-session/', {
                merchantId,
                merchantEmail,
                businessName,
            });
            
            return {
                sessionId: response.data.checkoutSessionId || null,
                url: response.data.url,
            }
        } catch (error) {
            const status = error?.response?.status
            const msg = error?.response?.data?.error || error?.message || ''
            if (status === 501 || String(msg).includes('NOT_IMPLEMENTED')) {
                return { sessionId: null, url: null }
            }
            throw new Error(error?.response?.data?.error || 'Failed to create checkout')
        }
    },

    // 5. Get onboarding summary
    async getOnboardingSummary(businessId) {
        console.log('📤 API: Getting onboarding summary', businessId)
        try {
            const response = await backendAPI.get(`/api/business/${businessId}/onboarding-summary/`)
            return response.data
        } catch (error) {
            throw new Error(error?.response?.data?.error || 'Failed to get summary')
        }
    },

    // 6. Complete onboarding
    async completeOnboarding(businessId) {
        console.log('📤 API: Completing onboarding', businessId)
        try {
            const response = await backendAPI.post(`/api/business/${businessId}/complete-onboarding/`)
            return response.data
        } catch (error) {
            throw new Error(error?.response?.data?.error || 'Failed to complete onboarding')
        }
    },

    // 6. Get user businesses
    async getUserBusinesses() {
        console.log('📤 API: Getting user businesses')
        try {
            const response = await backendAPI.get('/api/user-businesses/')
            return {
                success: true,
                businesses: response.data,
            }
        } catch (error) {
            throw new Error(error?.response?.data?.error || error?.message || 'Failed to get user businesses')
        }
    }
}

// Helper function to clear auth
export const clearAuth = () => {
    localStorage.removeItem('mockAuthToken')
}
